import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #cartUpdateRevision = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    if (rowsToRemove.length === this.refs.cartItemRows.length && this.#showOptimisticEmptyCart()) return;

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const shouldReduceMotion = prefersReducedMotion();
      const remove = () => {
        if (shouldReduceMotion || row.classList.contains('removing')) {
          row.remove();
        }
      };

      if (shouldReduceMotion) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Immediately displays the empty-cart state while the server confirms removal.
   * @returns {boolean} Whether the optimistic state was rendered.
   */
  #showOptimisticEmptyCart() {
    const drawer = this.closest('cart-drawer-component');
    const dialog = this.closest('dialog');
    const template = drawer?.querySelector('#cart-drawer-empty-template');

    if (!(dialog instanceof HTMLDialogElement) || !(template instanceof HTMLTemplateElement)) return false;

    this.replaceChildren(template.content.cloneNode(true));
    dialog.classList.add('cart-drawer--empty');
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading-empty');
    dialog.setAttribute('cart-summary-sticky', 'false');

    return true;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname + window.location.search,
    });

    cartTotal?.shimmer();
    const updateRevision = ++this.#cartUpdateRevision;

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        if (updateRevision !== this.#cartUpdateRevision) return;

        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          if (this.querySelector('[data-optimistic-empty-cart]')) {
            await this.#reconcileCartSection(null, updateRevision);
          } else {
            this.#handleCartError(line, parsedResponseText);
          }
          return;
        }

        const sectionHTML = parsedResponseText.sections?.[this.sectionId];
        const newSectionHTML = sectionHTML ? new DOMParser().parseFromString(sectionHTML, 'text/html') : null;

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : Number(parsedResponseText.item_count) || 0;

        const reconciled = await this.#reconcileCartSection(sectionHTML, updateRevision);
        if (!reconciled || updateRevision !== this.#cartUpdateRevision) return;

        this.#updateQuantitySelectors(parsedResponseText);
        this.#updateCartQuantitySelectorButtonStates();

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );
      })
      .catch(async (error) => {
        console.error(error);

        if (updateRevision === this.#cartUpdateRevision) {
          const reconciled = await this.#reconcileCartSection(null, updateRevision);

          if (reconciled && updateRevision === this.#cartUpdateRevision) {
            this.#updateCartQuantitySelectorButtonStates();
            await this.#dispatchRecoveredCartUpdate(updateRevision);
          }
        }
      })
      .finally(() => {
        if (updateRevision === this.#cartUpdateRevision) {
          this.#enableCartItems();
        }
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Reconciles cart markup from bundled, single-section, or full-page HTML.
   * @param {string | null | undefined} bundledSectionHTML - Section HTML returned with the cart mutation.
   * @param {number} updateRevision - Revision that owns this reconciliation.
   * @returns {Promise<boolean>} Whether fresh cart markup was applied.
   */
  async #reconcileCartSection(bundledSectionHTML, updateRevision) {
    if (updateRevision !== this.#cartUpdateRevision) return false;

    try {
      if (await this.#tryMorphCartHTML(bundledSectionHTML, 'bundled', updateRevision)) return true;
    } catch (error) {
      console.warn('[cart-items] Bundled section update failed:', error);
    }

    if (updateRevision !== this.#cartUpdateRevision) return false;

    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(
        this.sectionId,
        false,
        new URL(window.location.href)
      );

      if (updateRevision !== this.#cartUpdateRevision) return false;
      if (await this.#tryMorphCartHTML(sectionHTML, 'single-section', updateRevision)) return true;
    } catch (error) {
      console.warn('[cart-items] Single-section retry failed:', error);
    }

    if (updateRevision !== this.#cartUpdateRevision) return false;

    try {
      const pageURL = new URL(window.location.href);
      pageURL.searchParams.delete('section_id');
      pageURL.searchParams.delete('sections');

      const response = await fetch(pageURL, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) {
        throw new Error(`Full-page cart refresh failed with status ${response.status}`);
      }

      const pageHTML = await response.text();

      if (updateRevision !== this.#cartUpdateRevision) return false;
      if (await this.#tryMorphCartHTML(pageHTML, 'full-page', updateRevision)) return true;
    } catch (error) {
      console.error('[cart-items] Full-page cart refresh failed:', error);
    }

    if (updateRevision !== this.#cartUpdateRevision) return false;

    window.location.reload();
    return false;
  }

  /**
   * Applies cart markup from either a complete section or an extractable cart subtree.
   * @param {string | null | undefined} html - Fresh HTML containing the cart.
   * @param {string} source - The refresh stage used for diagnostics.
   * @param {number} updateRevision - Revision that owns this morph.
   * @returns {Promise<boolean>} Whether cart markup was found and applied.
   */
  async #tryMorphCartHTML(html, source, updateRevision) {
    if (updateRevision !== this.#cartUpdateRevision || typeof html !== 'string' || html.trim() === '') return false;

    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const currentDrawer = this.closest('cart-drawer-component');
    const incomingDrawer = fragment.querySelector('cart-drawer-component');

    if (currentDrawer && incomingDrawer) {
      if (updateRevision !== this.#cartUpdateRevision) return false;
      morph(currentDrawer, incomingDrawer);
      return true;
    }

    const sectionElement = fragment.getElementById(`shopify-section-${this.sectionId}`);

    if (sectionElement) {
      try {
        if (updateRevision !== this.#cartUpdateRevision) return false;
        await morphSection(this.sectionId, html);
        return true;
      } catch (error) {
        console.warn(`[cart-items] ${source} section morph failed; trying cart subtree:`, error);
      }
    }

    const incomingCartItems = Array.from(fragment.querySelectorAll('cart-items-component')).find(
      (element) => element instanceof HTMLElement && element.dataset.sectionId === this.sectionId
    );

    if (!incomingCartItems) return false;

    if (updateRevision !== this.#cartUpdateRevision) return false;
    morph(this, incomingCartItems);
    return true;
  }

  /**
   * Broadcasts fresh cart JSON after recovering from a failed mutation response.
   * @param {number} updateRevision - Revision that owns this recovery.
   */
  async #dispatchRecoveredCartUpdate(updateRevision) {
    if (updateRevision !== this.#cartUpdateRevision) return;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      if (updateRevision !== this.#cartUpdateRevision) return;

      this.dispatchEvent(
        new CartUpdateEvent(cart, this.sectionId, {
          itemCount: Number(cart.item_count) || 0,
          source: 'cart-items-recovery',
        })
      );
    } catch (error) {
      console.warn('[cart-items] Failed to broadcast recovered cart state:', error);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    for (const row of this.querySelectorAll('.cart-items__table-row.removing')) {
      row.classList.remove('removing');
      row.style.removeProperty('--row-height');
    }

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event.target === this) return;

    const updateRevision = ++this.#cartUpdateRevision;
    const eventData = event.detail.data ?? event.detail.resource;
    const shouldKeepCheckoutDrawerOpen = Number(eventData?.itemCount ?? eventData?.item_count) > 0 && !eventData?.didError;
    const cartItemsHtml = eventData?.sections?.[this.sectionId];

    try {
      const reconciled = await this.#reconcileCartSection(cartItemsHtml, updateRevision);
      if (!reconciled || updateRevision !== this.#cartUpdateRevision) return;

      this.#updateCartQuantitySelectorButtonStates();

      if (shouldKeepCheckoutDrawerOpen) {
        requestAnimationFrame(() => document.dispatchEvent(new CustomEvent('cart:open-checkout-drawer')));
      }
    } catch (error) {
      console.error('[cart-items] Cart update reconciliation failed:', error);
    } finally {
      if (updateRevision === this.#cartUpdateRevision) {
        this.#enableCartItems();
      }
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
